/**
 * FULLY FUNCTIONAL SUBSCRIPTION & PLAN PAGE
 *
 * Features:
 * - Desktop: License details, verification timeline, days left
 * - Cloud: Plan management, upgrade/downgrade, payment processing
 * - Usage tracking with real data
 * - Payment history
 * - Multiple payment providers (Chargily, Stripe)
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CreditCard,
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Calendar,
  Clock,
  Users,
  HardDrive,
  Cloud,
  Monitor,
  CheckCircle,
  AlertCircle,
  ArrowUpRight,
  Sparkles,
  RefreshCw,
  Copy,
  Check,
  BarChart,
  FileText,
  Package,
  History,
  Wallet,
  ChevronRight,
  AlertTriangle,
  Lock,
  Unlock,
  ExternalLink,
  CreditCard as PaymentIcon,
} from 'lucide-react';
import { useLicenseStore } from '@/stores/licenseStore';
import { useAuthStore } from '@/stores/authStore';
import { isTauri } from '@/lib/tauri';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SubscriptionService } from '@/services/subscriptionService';
import { PaymentService } from '@/services/paymentService';
import type { LicensePlan } from '@lebusiness/types';

export const Route = createFileRoute('/settings/subscription/')({
  component: SubscriptionPage,
});

// Plan types configuration matching database
const PLANS: Record<string, LicensePlan & { features: string[]; limits: { users: number; invoices: number; products: number; storage: number } }> = {
  free: {
    id: 'free',
    name: 'Gratuit',
    tierLevel: 0,
    maxUsers: 1,
    maxInvoicesPerMonth: 100,
    enabledFeatures: ['products', 'clients', 'basic_invoices'],
    priceMonthlyDzd: 0,
    priceYearlyDzd: 0,
    isActive: true,
    description: 'Pour démarrer',
    createdAt: new Date(),
    updatedAt: new Date(),
    features: [
      '1 utilisateur',
      '100 factures/mois',
      '50 produits',
      'Rapports basiques',
      'Support communautaire',
    ],
    limits: {
      users: 1,
      invoices: 100,
      products: 50,
      storage: 100,
    },
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    tierLevel: 1,
    maxUsers: 3,
    maxInvoicesPerMonth: 500,
    enabledFeatures: ['products', 'clients', 'invoices', 'reports', 'treasury'],
    priceMonthlyDzd: 2900,
    priceYearlyDzd: 29000,
    isActive: true,
    description: 'Pour les petites entreprises',
    createdAt: new Date(),
    updatedAt: new Date(),
    features: [
      '3 utilisateurs',
      '500 factures/mois',
      '200 produits',
      'Rapports avancés',
      'Support email',
      'POS inclus',
    ],
    limits: {
      users: 3,
      invoices: 500,
      products: 200,
      storage: 500,
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    tierLevel: 2,
    maxUsers: 10,
    maxInvoicesPerMonth: -1,
    enabledFeatures: ['*'],
    priceMonthlyDzd: 7900,
    priceYearlyDzd: 79000,
    isActive: true,
    description: 'Pour les entreprises en croissance',
    createdAt: new Date(),
    updatedAt: new Date(),
    features: [
      '10 utilisateurs',
      'Factures illimitées',
      'Produits illimités',
      'Tous les rapports',
      'Support prioritaire',
      'POS + Multi-caisse',
      'API access',
    ],
    limits: {
      users: 10,
      invoices: -1,
      products: -1,
      storage: 5000,
    },
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    tierLevel: 3,
    maxUsers: 100,
    maxInvoicesPerMonth: -1,
    enabledFeatures: ['*'],
    priceMonthlyDzd: 25000,
    priceYearlyDzd: 250000,
    isActive: true,
    description: 'Pour les grandes entreprises',
    createdAt: new Date(),
    updatedAt: new Date(),
    features: [
      'Utilisateurs illimités',
      'Tout illimité',
      'Support dédié 24/7',
      'Formation sur site',
      'Personnalisations',
      'SLA garanti',
    ],
    limits: {
      users: -1,
      invoices: -1,
      products: -1,
      storage: -1,
    },
  },
};

function SubscriptionPage() {
  // Desktop license state
  const { license, licenseState, lastVerifiedAt, verificationDueAt, gracePeriodEndsAt, daysUntilExpiry } = useLicenseStore();
  
  // Cloud subscription state
  const { company, profile } = useAuthStore();
  const [currentPlan, setCurrentPlan] = useState<string>('starter');
  const [plans, setPlans] = useState<LicensePlan[]>([]);
  const [usage, setUsage] = useState({
    users: 2,
    invoices: 127,
    products: 45,
    storage: 234,
  });
  const [isLoading, setIsLoading] = useState(true);
  
  // Plan change dialog state
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState<'chargily' | 'stripe'>('chargily');
  
  // Payment history
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  
  const isDesktop = isTauri();
  const [copiedKey, setCopiedKey] = useState(false);

  // Load subscription data
  useEffect(() => {
    loadSubscriptionData();
  }, [company?.id]);

  const loadSubscriptionData = async () => {
    if (!company?.id) return;
    
    setIsLoading(true);
    try {
      // Load available plans
      const availablePlans = await SubscriptionService.getPlans();
      setPlans(availablePlans);
      
      // Load current subscription
      const subscription = await SubscriptionService.getCurrentSubscription(company.id);
      if (subscription) {
        setCurrentPlan(subscription.plan.name.toLowerCase());
      }
      
      // Load usage stats
      const usageStats = await SubscriptionService.getCurrentUsage(company.id);
      setUsage({
        users: usageStats.usersCount,
        invoices: usageStats.invoicesCount,
        products: 0, // TODO: Fetch from products API
        storage: usageStats.storageUsedMb,
      });
      
      // Load payment history
      const history = await PaymentService.getPaymentHistory(company.id);
      setPaymentHistory(history);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const plan = PLANS[currentPlan] || PLANS.starter;

  const copyLicenseKey = () => {
    if (license?.license_key) {
      navigator.clipboard.writeText(license.license_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const getLicenseStatusConfig = () => {
    switch (licenseState) {
      case 'valid':
        return { 
          icon: ShieldCheck, 
          color: 'text-green-500', 
          bg: 'bg-green-100 dark:bg-green-900/30', 
          label: 'Valide', 
          labelAr: 'صالح',
          borderColor: 'border-green-200 dark:border-green-800'
        };
      case 'verification_needed':
        return { 
          icon: ShieldAlert, 
          color: 'text-amber-500', 
          bg: 'bg-amber-100 dark:bg-amber-900/30', 
          label: 'Vérification requise', 
          labelAr: 'يتطلب التحقق',
          borderColor: 'border-amber-200 dark:border-amber-800'
        };
      case 'expired':
        return { 
          icon: ShieldX, 
          color: 'text-red-500', 
          bg: 'bg-red-100 dark:bg-red-900/30', 
          label: 'Expiré', 
          labelAr: 'منتهي',
          borderColor: 'border-red-200 dark:border-red-800'
        };
      default:
        return { 
          icon: Shield, 
          color: 'text-gray-500', 
          bg: 'bg-gray-100 dark:bg-gray-900/30', 
          label: 'Inconnu', 
          labelAr: 'غير معروف',
          borderColor: 'border-gray-200 dark:border-gray-800'
        };
    }
  };

  const licenseConfig = getLicenseStatusConfig();

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return 'N/A';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (dateString?: string | Date) => {
    if (!dateString) return 'N/A';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
    });
  };

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === -1) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-500';
    if (percentage >= 70) return 'text-amber-500';
    return 'text-green-500';
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-amber-500';
    return 'bg-green-500';
  };

  // Calculate days until expiry
  const daysLeft = license?.expires_at 
    ? Math.ceil((new Date(license.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  // Handle plan change
  const handlePlanChange = async () => {
    if (!company?.id || !selectedPlan) return;
    
    setIsProcessing(true);
    try {
      const targetPlanId = plans.find(p => p.name.toLowerCase() === selectedPlan)?.id;
      if (!targetPlanId) throw new Error('Plan not found');

      // Create plan change request
      const request = await SubscriptionService.requestPlanChange(
        company.id,
        targetPlanId,
        billingCycle
      );

      // If prorated amount is 0 or negative (downgrade), apply immediately
      if (request.proratedAmount <= 0) {
        await SubscriptionService.applyPlanChange(request.requestId);
        await loadSubscriptionData();
        setShowPlanDialog(false);
        return;
      }

      // Otherwise, create payment
      const paymentIntent = await PaymentService.createPaymentIntent({
        amount: request.proratedAmount,
        description: `Changement de plan vers ${selectedPlan.toUpperCase()}`,
        provider: paymentProvider,
        metadata: {
          company_id: company.id,
          request_id: request.requestId,
          plan: selectedPlan,
          billing_cycle: billingCycle,
        },
        successUrl: `${window.location.origin}/settings/subscription?success=true`,
        cancelUrl: `${window.location.origin}/settings/subscription?cancelled=true`,
      });

      // Redirect to payment
      if (paymentIntent.checkoutUrl) {
        window.location.href = paymentIntent.checkoutUrl;
      }
    } catch (error) {
      console.error('Plan change failed:', error);
      alert('Erreur lors du changement de plan. Veuillez réessayer.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate prorated price display
  const calculatePriceDisplay = (planKey: string) => {
    const targetPlan = PLANS[planKey];
    if (!targetPlan) return null;
    
    const basePrice = billingCycle === 'yearly' 
      ? targetPlan.priceYearlyDzd 
      : targetPlan.priceMonthlyDzd;
    
    // Calculate savings for yearly
    const monthlyTotal = targetPlan.priceMonthlyDzd * 12;
    const yearlySavings = monthlyTotal - targetPlan.priceYearlyDzd;
    
    return {
      basePrice,
      yearlySavings: billingCycle === 'yearly' ? yearlySavings : 0,
    };
  };

  // Super Admin gets a special view
  if (profile?.isSuperAdmin) {
    return (
      <DashboardLayout>
        <TooltipProvider>
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-2">
                  <Shield className="h-8 w-8 text-violet-500" />
                  Super Admin / المشرف العام
                </h1>
                <p className="text-muted-foreground mt-2">
                  Accès complet à toutes les fonctionnalités et modules
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" asChild>
                  <Link to="/settings">
                    Retour aux paramètres
                  </Link>
                </Button>
              </div>
            </div>

            {/* Super Admin Status Card */}
            <Card className="border-violet-200 dark:border-violet-800 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-violet-500" />
                    Privilèges Super Admin
                  </CardTitle>
                  <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300 text-xs">
                    Illimité
                  </Badge>
                </div>
                <CardDescription>
                  {company?.name || 'Plateforme leBusiness'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4 p-4 bg-white/60 dark:bg-white/5 rounded-lg border border-violet-100 dark:border-violet-800">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <Unlock className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">Accès illimité</p>
                    <p className="text-sm text-muted-foreground">
                      En tant que Super Admin, vous avez accès à toutes les fonctionnalités sans aucune restriction de plan ou de quota.
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/40 dark:bg-white/5">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Utilisateurs illimités</p>
                      <p className="text-xs text-muted-foreground">Aucune restriction sur le nombre d'utilisateurs</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/40 dark:bg-white/5">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Factures illimitées</p>
                      <p className="text-xs text-muted-foreground">Aucune limite de facturation mensuelle</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/40 dark:bg-white/5">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Tous les modules</p>
                      <p className="text-xs text-muted-foreground">POS, Comptabilité, RH, et tous les modules sectoriels</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/40 dark:bg-white/5">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Panel d'administration</p>
                      <p className="text-xs text-muted-foreground">Gestion des entreprises, utilisateurs et fonctionnalités</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/40 dark:bg-white/5">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Stockage illimité</p>
                      <p className="text-xs text-muted-foreground">Aucune restriction de stockage</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/40 dark:bg-white/5">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">API & Intégrations</p>
                      <p className="text-xs text-muted-foreground">Accès complet à l'API et toutes les intégrations</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cloud className="h-5 w-5" />
                  Informations du compte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Email</p>
                    <p className="font-medium truncate">{profile?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Rôle</p>
                    <p className="font-medium text-violet-600 dark:text-violet-400">Super Admin</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Entreprise</p>
                    <p className="font-medium">{company?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Membre depuis</p>
                    <p className="font-medium">
                      {formatDateShort((profile as any)?.createdAt || (profile as any)?.created_at)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TooltipProvider>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <TooltipProvider>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <CreditCard className="h-8 w-8" />
                Abonnement / الاشتراك
              </h1>
              <p className="text-muted-foreground mt-2">
                {isDesktop
                  ? 'Gérez votre licence desktop et consultez les informations de vérification'
                  : 'Gérez votre plan et consultez votre utilisation'
                }
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to="/settings">
                  Retour aux paramètres
                </Link>
              </Button>
            </div>
          </div>

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
              <TabsTrigger value="overview">Aperçu</TabsTrigger>
              <TabsTrigger value="plans">Plans</TabsTrigger>
              <TabsTrigger value="history">Historique</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Current Plan Card */}
                <Card className="md:col-span-1">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-amber-500" />
                        Plan Actuel
                      </CardTitle>
                      <Badge
                        variant="secondary"
                        className={cn(
                          "text-xs",
                          currentPlan === 'starter' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                          currentPlan === 'pro' && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
                          currentPlan === 'enterprise' && "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
                          currentPlan === 'free' && "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"
                        )}
                      >
                        {plan.name}
                      </Badge>
                    </div>
                    <CardDescription>
                      {company?.name || 'Votre entreprise'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold">
                        {plan.priceMonthlyDzd > 0
                          ? plan.priceMonthlyDzd.toLocaleString('fr-DZ')
                          : 'Gratuit'}
                      </span>
                      {plan.priceMonthlyDzd > 0 && (
                        <span className="text-muted-foreground">DZD/mois</span>
                      )}
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <p className="text-sm font-medium">Fonctionnalités incluses:</p>
                      <ul className="space-y-1">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => {
                        setSelectedPlan(currentPlan);
                        setShowPlanDialog(true);
                      }}
                    >
                      <ArrowUpRight className="h-4 w-4 mr-2" />
                      Changer de plan
                    </Button>
                  </CardFooter>
                </Card>

                {/* License/Subscription Card */}
                {isDesktop && license ? (
                  <Card className="md:col-span-1">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          <Monitor className="h-5 w-5" />
                          Licence Desktop
                        </CardTitle>
                        <Badge 
                          variant="secondary" 
                          className={cn("text-xs", licenseConfig.bg, licenseConfig.color)}
                        >
                          <licenseConfig.icon className="h-3 w-3 mr-1" />
                          {licenseConfig.label}
                        </Badge>
                      </div>
                      <CardDescription>
                        Informations de votre licence logicielle
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Days Left Alert */}
                      {daysLeft !== null && daysLeft <= 30 && (
                        <Alert variant={daysLeft <= 7 ? "destructive" : "default"} className={cn(
                          daysLeft <= 7 
                            ? "border-red-200 bg-red-50 dark:bg-red-900/20" 
                            : "border-amber-200 bg-amber-50 dark:bg-amber-900/20"
                        )}>
                          <AlertTriangle className="h-4 w-4" />
                          <AlertTitle>
                            {daysLeft <= 0 
                              ? 'Licence expirée' 
                              : daysLeft === 1 
                                ? '1 jour restant'
                                : `${daysLeft} jours restants`
                            }
                          </AlertTitle>
                          <AlertDescription>
                            {daysLeft <= 0 
                              ? 'Votre licence a expiré. Veuillez renouveler pour continuer.'
                              : `Votre licence expire le ${formatDate(license.expires_at)}`
                            }
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* License Key */}
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Clé de licence</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 px-3 py-2 bg-muted rounded-md font-mono text-sm truncate">
                            {license.license_key}
                          </code>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={copyLicenseKey}>
                                {copiedKey ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {copiedKey ? 'Copié!' : 'Copier la clé'}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>

                      <Separator />

                      {/* License Details */}
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Type</p>
                          <p className="font-medium flex items-center gap-1">
                            {license.license_type === 'desktop' && <Monitor className="h-4 w-4" />}
                            {license.license_type === 'cloud' && <Cloud className="h-4 w-4" />}
                            {license.license_type === 'hybrid' && <HardDrive className="h-4 w-4" />}
                            {license.license_type?.toUpperCase() || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Statut</p>
                          <p className="font-medium capitalize">{license.status || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Appareil</p>
                          <p className="font-medium truncate">{license.device_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Max appareils</p>
                          <p className="font-medium">{license.max_devices || 1}</p>
                        </div>
                      </div>

                      <Separator />

                      {/* Verification Timeline */}
                      <div className="space-y-3">
                        <p className="text-sm font-medium flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Calendrier de vérification
                        </p>
                        
                        {/* Timeline */}
                        <div className="relative pl-6 space-y-4">
                          {/* Last Verification */}
                          <div className="relative">
                            <div className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-green-500 border-2 border-background" />
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Dernière vérification</p>
                              <p className="text-sm font-medium">
                                {lastVerifiedAt ? formatDate(lastVerifiedAt) : 'Jamais'}
                              </p>
                            </div>
                          </div>

                          {/* Next Verification */}
                          <div className="relative">
                            <div className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-background animate-pulse" />
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Prochaine vérification</p>
                              <p className="text-sm font-medium">
                                {verificationDueAt ? formatDate(verificationDueAt) : 'N/A'}
                              </p>
                              {verificationDueAt && (
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  Dans {Math.ceil((new Date(verificationDueAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} jours
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Expiry Date */}
                          <div className="relative">
                            <div className={cn(
                              "absolute -left-6 top-1 w-3 h-3 rounded-full border-2 border-background",
                              daysLeft !== null && daysLeft <= 7 ? "bg-red-500" : "bg-amber-500"
                            )} />
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">Expiration de la licence</p>
                              <p className="text-sm font-medium">
                                {license.expires_at ? formatDate(license.expires_at) : 'N/A'}
                              </p>
                              {daysLeft !== null && (
                                <p className={cn(
                                  "text-xs",
                                  daysLeft <= 7 ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"
                                )}>
                                  {daysLeft <= 0 
                                    ? 'Expiré'
                                    : daysLeft === 1 
                                      ? '1 jour restant'
                                      : `${daysLeft} jours restants`
                                  }
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Warning if verification needed */}
                      {licenseState === 'verification_needed' && (
                        <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
                          <AlertCircle className="h-4 w-4 text-amber-500" />
                          <AlertTitle>Vérification requise</AlertTitle>
                          <AlertDescription>
                            Connectez-vous à Internet pour vérifier votre licence.
                            {gracePeriodEndsAt && ` Période de grâce jusqu'au ${formatDate(gracePeriodEndsAt)}.`}
                          </AlertDescription>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="md:col-span-1">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Cloud className="h-5 w-5" />
                        Compte Cloud
                      </CardTitle>
                      <CardDescription>
                        Informations de votre abonnement cloud
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Email</p>
                          <p className="font-medium truncate">{profile?.email || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Rôle</p>
                          <p className="font-medium">
                            {profile?.isSuperAdmin ? 'Super Admin' : (profile as any)?.isAdmin ? 'Admin' : 'Utilisateur'}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Entreprise</p>
                          <p className="font-medium">{company?.name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Membre depuis</p>
                          <p className="font-medium">
                            {formatDateShort((profile as any)?.createdAt || (profile as any)?.created_at)}
                          </p>
                        </div>
                      </div>

                      <Separator />

                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Monitor className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                              Version Desktop disponible
                            </p>
                            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                              Téléchargez l'application desktop pour travailler hors ligne avec synchronisation automatique.
                            </p>
                            <Button size="sm" variant="outline" className="mt-2">
                              Télécharger
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Usage Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart className="h-5 w-5" />
                    Utilisation ce mois
                  </CardTitle>
                  <CardDescription>
                    Suivez votre consommation par rapport aux limites de votre plan
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Users */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          Utilisateurs
                        </span>
                        <span className={cn("text-sm font-medium", getUsageColor(getUsagePercentage(usage.users, plan.limits.users)))}>
                          {usage.users} / {plan.limits.users === -1 ? '∞' : plan.limits.users}
                        </span>
                      </div>
                      <Progress 
                        value={getUsagePercentage(usage.users, plan.limits.users)} 
                        className="h-2"
                      />
                    </div>

                    {/* Invoices */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm flex items-center gap-1">
                          <FileText className="h-4 w-4" />
                          Factures
                        </span>
                        <span className={cn("text-sm font-medium", getUsageColor(getUsagePercentage(usage.invoices, plan.limits.invoices)))}>
                          {usage.invoices} / {plan.limits.invoices === -1 ? '∞' : plan.limits.invoices}
                        </span>
                      </div>
                      <Progress 
                        value={getUsagePercentage(usage.invoices, plan.limits.invoices)} 
                        className="h-2"
                      />
                    </div>

                    {/* Products */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm flex items-center gap-1">
                          <Package className="h-4 w-4" />
                          Produits
                        </span>
                        <span className={cn("text-sm font-medium", getUsageColor(getUsagePercentage(usage.products, plan.limits.products)))}>
                          {usage.products} / {plan.limits.products === -1 ? '∞' : plan.limits.products}
                        </span>
                      </div>
                      <Progress 
                        value={getUsagePercentage(usage.products, plan.limits.products)} 
                        className="h-2"
                      />
                    </div>

                    {/* Storage */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm flex items-center gap-1">
                          <HardDrive className="h-4 w-4" />
                          Stockage
                        </span>
                        <span className={cn("text-sm font-medium", getUsageColor(getUsagePercentage(usage.storage, plan.limits.storage)))}>
                          {usage.storage} MB / {plan.limits.storage === -1 ? '∞' : `${plan.limits.storage} MB`}
                        </span>
                      </div>
                      <Progress 
                        value={getUsagePercentage(usage.storage, plan.limits.storage)} 
                        className="h-2"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="plans" className="space-y-6">
              {/* Plan Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle>Nos Plans</CardTitle>
                  <CardDescription>
                    Choisissez le plan qui correspond à vos besoins
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {Object.entries(PLANS).map(([key, p]) => {
                      const isCurrentPlan = key === currentPlan;
                      const priceInfo = calculatePriceDisplay(key);
                      
                      return (
                        <div
                          key={key}
                          className={cn(
                            "border rounded-lg p-4 space-y-3 transition-all relative",
                            isCurrentPlan && "border-primary bg-primary/5 ring-2 ring-primary/20",
                            !isCurrentPlan && "hover:border-primary/50 hover:shadow-md"
                          )}
                        >
                          {isCurrentPlan && (
                            <Badge className="absolute -top-2 left-4 bg-primary text-primary-foreground">
                              Actuel
                            </Badge>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold">{p.name}</h3>
                          </div>
                          
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold">
                              {p.priceMonthlyDzd === 0 
                                ? 'Gratuit' 
                                : `${p.priceMonthlyDzd.toLocaleString()}`}
                            </span>
                            {p.priceMonthlyDzd > 0 && (
                              <span className="text-xs text-muted-foreground">DZD/mois</span>
                            )}
                          </div>

                          {priceInfo && priceInfo.yearlySavings > 0 && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                              Économisez {priceInfo.yearlySavings.toLocaleString()} DA/an
                            </Badge>
                          )}
                          
                          <ul className="space-y-1 text-xs text-muted-foreground">
                            {p.features.slice(0, 4).map((f, i) => (
                              <li key={i} className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                                {f}
                              </li>
                            ))}
                            {p.features.length > 4 && (
                              <li className="text-muted-foreground">+{p.features.length - 4} autres...</li>
                            )}
                          </ul>
                          
                          {!isCurrentPlan && (
                            <Button 
                              size="sm" 
                              variant={key === 'pro' ? 'default' : 'outline'} 
                              className="w-full"
                              onClick={() => {
                                setSelectedPlan(key);
                                setShowPlanDialog(true);
                              }}
                            >
                              {key === 'enterprise' ? 'Contactez-nous' : 'Choisir'}
                            </Button>
                          )}
                          
                          {isCurrentPlan && (
                            <Button size="sm" variant="secondary" className="w-full" disabled>
                              Plan actuel
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history" className="space-y-6">
              {/* Payment History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Historique des paiements
                  </CardTitle>
                  <CardDescription>
                    Consultez l'historique de vos paiements et factures
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {paymentHistory.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Aucun paiement trouvé</p>
                      <p className="text-sm">Vos paiements apparaîtront ici</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {paymentHistory.map((payment) => (
                        <div 
                          key={payment.id} 
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className={cn(
                              "w-10 h-10 rounded-full flex items-center justify-center",
                              payment.status === 'completed' ? "bg-green-100 text-green-600" : "bg-amber-100 text-amber-600"
                            )}>
                              <PaymentIcon className="h-5 w-5" />
                            </div>
                            <div>
                              <p className="font-medium">{payment.description}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(payment.createdAt)} • {payment.provider}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{PaymentService.formatAmount(payment.amount, payment.currency)}</p>
                            <Badge 
                              variant={payment.status === 'completed' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {payment.status === 'completed' ? 'Payé' : payment.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Plan Change Dialog */}
          <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Changer de plan</DialogTitle>
                <DialogDescription>
                  Sélectionnez le plan qui correspond le mieux à vos besoins
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Billing Cycle Toggle */}
                <div className="flex items-center justify-center gap-4 p-4 bg-muted rounded-lg">
                  <span className={cn("text-sm", billingCycle === 'monthly' && "font-medium")}>Mensuel</span>
                  <button
                    onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                    className={cn(
                      "relative w-14 h-7 rounded-full transition-colors",
                      billingCycle === 'yearly' ? "bg-primary" : "bg-muted-foreground/30"
                    )}
                  >
                    <span
                      className={cn(
                        "absolute top-1 w-5 h-5 rounded-full bg-white transition-transform",
                        billingCycle === 'yearly' ? "translate-x-8" : "translate-x-1"
                      )}
                    />
                  </button>
                  <span className={cn("text-sm", billingCycle === 'yearly' && "font-medium")}>
                    Annuel
                    <Badge variant="secondary" className="ml-2 text-xs bg-green-100 text-green-700">
                      -17%
                    </Badge>
                  </span>
                </div>

                {/* Plan Selection */}
                <RadioGroup value={selectedPlan} onValueChange={setSelectedPlan} className="grid gap-4 sm:grid-cols-2">
                  {Object.entries(PLANS).filter(([key]) => key !== 'free').map(([key, p]) => {
                    const isSelected = selectedPlan === key;
                    const price = billingCycle === 'yearly' ? p.priceYearlyDzd : p.priceMonthlyDzd;
                    
                    return (
                      <div key={key}>
                        <RadioGroupItem value={key} id={key} className="peer sr-only" />
                        <Label
                          htmlFor={key}
                          className={cn(
                            "flex flex-col p-4 border rounded-lg cursor-pointer transition-all",
                            isSelected 
                              ? "border-primary bg-primary/5 ring-2 ring-primary/20" 
                              : "border-muted hover:border-primary/50"
                          )}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">{p.name}</span>
                            {isSelected && <Check className="h-4 w-4 text-primary" />}
                          </div>
                          <div className="flex items-baseline gap-1 mb-3">
                            <span className="text-xl font-bold">{price.toLocaleString()}</span>
                            <span className="text-xs text-muted-foreground">DZD/{billingCycle === 'yearly' ? 'an' : 'mois'}</span>
                          </div>
                          <ul className="space-y-1 text-xs text-muted-foreground">
                            {p.features.slice(0, 3).map((f, i) => (
                              <li key={i} className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                                {f}
                              </li>
                            ))}
                          </ul>
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>

                {/* Payment Provider Selection */}
                {selectedPlan && PLANS[selectedPlan]?.priceMonthlyDzd > 0 && (
                  <div className="space-y-3">
                    <Label>Méthode de paiement</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setPaymentProvider('chargily')}
                        className={cn(
                          "p-4 border rounded-lg text-left transition-all",
                          paymentProvider === 'chargily' 
                            ? "border-primary bg-primary/5" 
                            : "border-muted hover:border-primary/50"
                        )}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Wallet className="h-5 w-5" />
                          <span className="font-medium">Chargily</span>
                        </div>
                        <p className="text-xs text-muted-foreground">CCP, BaridiMob, EDAHABIA</p>
                      </button>
                      
                      <button
                        onClick={() => setPaymentProvider('stripe')}
                        className={cn(
                          "p-4 border rounded-lg text-left transition-all",
                          paymentProvider === 'stripe' 
                            ? "border-primary bg-primary/5" 
                            : "border-muted hover:border-primary/50"
                        )}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="h-5 w-5" />
                          <span className="font-medium">Stripe</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Carte bancaire internationale</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={handlePlanChange}
                  disabled={!selectedPlan || selectedPlan === currentPlan || isProcessing}
                >
                  {isProcessing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="h-4 w-4 mr-2" />
                  )}
                  {isProcessing ? 'Traitement...' : 'Procéder au paiement'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </TooltipProvider>
    </DashboardLayout>
  );
}

export default SubscriptionPage;
